import {
  dispatchMutationLifecycleEvent,
  MutationLifecycleEvent,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
} from '@github-ui/mutation-lifecycle-events'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {unpinIssueMutation, unpinIssueMutation$data} from './__generated__/unpinIssueMutation.graphql'

/** Packages a mutation response into the `{id, values}` shape the event carries, or
 * `undefined` if the affected node is missing (e.g. an unexpectedly null payload field). */
function toObjectPropertiesPatch(
  issueId: string,
  data: unpinIssueMutation$data,
  repositoryId?: string,
): ObjectPropertiesPatch | undefined {
  const issue = data.unpinIssue?.issue
  if (!issue) return undefined
  const {isPinned} = issue
  if (isPinned == null) return undefined

  const values: Record<string, unknown> = {isPinned}

  const pinnedIssuesTotalCount = issue.repository?.pinnedIssues?.totalCount
  if (repositoryId !== undefined && pinnedIssuesTotalCount !== undefined) {
    values.repository = {
      id: repositoryId,
      pinnedIssues: {totalCount: pinnedIssuesTotalCount},
    }
  }

  return {id: issueId, values}
}

export function commitUnpinIssueMutation({
  environment,
  input,
  repositoryId,
  currentPinnedCount,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {issueId: string}
  repositoryId?: string
  currentPinnedCount?: number
  onCompleted?: () => void
  onError?: (error: Error) => void
}) {
  const optimisticResponse =
    repositoryId !== undefined && currentPinnedCount !== undefined
      ? {
          unpinIssue: {
            id: null,
            issue: {
              id: input.issueId,
              isPinned: false,
              repository: {
                id: repositoryId,
                pinnedIssues: {
                  totalCount: Math.max(0, currentPinnedCount - 1),
                },
              },
            },
          },
        }
      : undefined

  // The optimistic patch reflects the scalar change the unpin mutation makes.
  // `issueId` is always present. Repository data is included when available so
  // consumers can keep both the issue and repository caches in sync.
  const optimisticValues: Record<string, unknown> = {isPinned: false}
  if (repositoryId !== undefined && currentPinnedCount !== undefined) {
    optimisticValues.repository = {
      id: repositoryId,
      pinnedIssues: {totalCount: Math.max(0, currentPinnedCount - 1)},
    }
  }
  const optimisticPatch: ObjectPropertiesPatch = {id: input.issueId, values: optimisticValues}
  const optimisticEvent = new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
    phase: 'optimistic',
    data: optimisticPatch,
  })
  const mutationId = optimisticEvent.detail.mutationId

  const disposable = commitMutation<unpinIssueMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation unpinIssueMutation($input: UnpinIssueInput!) @raw_response_type {
        unpinIssue(input: $input) {
          id @deleteRecord
          issue {
            isPinned
            repository {
              pinnedIssues(first: 3) {
                totalCount
              }
            }
          }
        }
      }
    `,
    variables: {
      input,
    },
    optimisticResponse,
    onCompleted: response => {
      const patch = toObjectPropertiesPatch(input.issueId, response, repositoryId)
      if (patch) {
        dispatchMutationLifecycleEvent(
          new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
            phase: 'completed',
            mutationId,
            data: patch,
          }),
        )
      }
      onCompleted?.()
    },
    onError: error => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'error', mutationId, error}),
      )
      onError?.(error)
    },
  })

  // Relay applies `optimisticResponse` synchronously as part of `commitMutation`,
  // so it's safe to dispatch the optimistic phase event right after committing.
  dispatchMutationLifecycleEvent(optimisticEvent)

  return disposable
}
